import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, Info, CheckCircle2, HelpCircle, Loader2, Utensils } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// A minimal, self-contained public client (no app shell, no auth, no interceptors).
function DishCard({ dish }) {
  if (dish.state === "verified" || dish.state === "verified_no_uk14") {
    const contains = dish.contains || [];
    const may = dish.may_contain || [];
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="allergen-dish">
        <div className="font-semibold text-slate-800">{dish.name}</div>
        {dish.state === "verified_no_uk14" ? (
          <div className="mt-2 flex items-start gap-2 text-sm text-slate-600">
            <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" />
            <span>{dish.message}</span>
          </div>
        ) : contains.length > 0 ? (
          <div className="mt-2 space-y-2">
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-red-700 flex items-center gap-1"><AlertTriangle size={12} /> Contains</div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {contains.map((a) => (
                  <span key={a.key} className="px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200" data-testid={`contains-${a.key}`}>{a.label}</span>
                ))}
              </div>
            </div>
          </div>
        ) : null}
        {may.length > 0 && (
          <div className="mt-2">
            <div className="text-xs font-bold uppercase tracking-wide text-amber-700 flex items-center gap-1"><Info size={12} /> May contain</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {may.map((a) => (
                <span key={a.key} className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200" data-testid={`may-${a.key}`}>{a.label}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
  const isReview = dish.state === "review";
  return (
    <div className={`rounded-xl border p-4 ${isReview ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-slate-50"}`} data-testid="allergen-dish">
      <div className="font-semibold text-slate-800">{dish.name}</div>
      <div className="mt-2 flex items-start gap-2 text-sm text-slate-700">
        {isReview ? <HelpCircle size={16} className="text-amber-600 mt-0.5 shrink-0" /> : <AlertTriangle size={16} className="text-slate-500 mt-0.5 shrink-0" />}
        <span>{dish.message}</span>
      </div>
    </div>
  );
}

export function PublicMenuView({ data }) {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="allergen-menu-public">
      <div className="max-w-md mx-auto px-4 py-6">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-brand/10 text-brand mb-2"><Utensils size={22} /></div>
          <h1 className="font-display text-2xl font-bold text-slate-900" data-testid="allergen-menu-restaurant">{data.restaurant_name}</h1>
          {data.site_name && <div className="text-sm text-slate-500">{data.site_name}</div>}
          <div className="mt-1 text-lg font-semibold text-slate-700">{data.menu_name}</div>
          <div className="text-xs text-slate-400">Information current at {new Date(data.updated_at).toLocaleString("en-GB")}</div>
        </div>

        <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3 flex items-start gap-2" data-testid="allergen-menu-notice">
          <AlertTriangle size={18} className="text-amber-600 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-900 font-medium">{data.notice}</p>
        </div>

        <div className="mt-5 space-y-6">
          {data.sections.map((sec, si) => (
            <div key={si} data-testid={`allergen-section-${si}`}>
              <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-2">{sec.name}</h2>
              <div className="space-y-3">
                {sec.items.map((d) => <DishCard key={d.id} dish={d} />)}
              </div>
            </div>
          ))}
        </div>

        <p className="mt-6 text-xs text-slate-500 leading-relaxed" data-testid="allergen-menu-disclaimer">{data.disclaimer}</p>
        <div className="mt-4 text-center text-[11px] text-slate-400">Powered by {data.powered_by}</div>
      </div>
    </div>
  );
}

export default function AllergenMenu() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Allergen Information";
    axios.get(`${BACKEND}/api/allergen-qr/resolve/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setError("This menu is not available. Please ask a member of staff."))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="animate-spin text-brand" /></div>;

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center" data-testid="allergen-menu-error">
      <AlertTriangle className="text-amber-500" size={30} />
      <p className="mt-3 text-slate-600 max-w-xs">{error}</p>
    </div>
  );

  return <PublicMenuView data={data} />;
}
